import React, { FC, ReactNode } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { MaximizeIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { ScenarioHooks, useFormat, useScreenSize } from '../../../hooks/index';
import {
  Scenario,
  ConversionSummary as ScenarioConversionSummary,
  ScenarioType,
} from '../../../types/scenario-modeling.types';
import { toMathRound } from '../../../utils/to-math-round.util';
import { useScenarioCapTableStore } from '../cap-table.store';
import { useScenarioStore } from '../scenario-modeling.store';
import {
  customSafesFormSchema,
  FundraisingFormSchema,
  investmentsFormSchema,
  poolsFormSchema,
} from './fundraising-validation';
import { LiquidityFormSchema } from './liquidity-validation';
import { valuationFormSchema } from './scenario-details.validation';

type FormSchema = FundraisingFormSchema & LiquidityFormSchema;

export type ConversionSummaryProps = {
  control: Control<FormSchema>;
  conversionSummary?: ScenarioConversionSummary;
  currencySign?: string;
  isCapTableLoading: boolean;
  isCapTablePending: boolean;
  isConversionRefused: boolean;
  isLoadingSummary: boolean;
  onOpenCapTable: () => void;
  scenario?: Scenario;
};

export const ConversionSummary: FC<ConversionSummaryProps> = ({
  control,
  conversionSummary,
  scenario,
  currencySign,
  onOpenCapTable,
  isCapTablePending,
  isCapTableLoading,
  isConversionRefused,
  isLoadingSummary,
}) => {
  const pointerBlockedStyles = useScenarioStore(({ pointerBlockedStyles }) => pointerBlockedStyles);
  const { format } = useFormat();
  const { isMobile: isTablet } = useScreenSize();
  const scenarioId = scenario?.id || '';
  const isScenarioHadCapTable = Boolean(scenario?.isGeneratedFirstCapTable);
  const { data: capTableData } = ScenarioHooks.useCapTableShareClasses({ scenarioId });
  const { valuation, customSafes, investments, pools } = useWatch<FormSchema>({
    control,
  });

  const { scheduled } = useScenarioCapTableStore();

  const parsedValuation = valuationFormSchema.safeParse(valuation);
  const { success: customSafesSuccess } = customSafesFormSchema.safeParse(customSafes);
  const { success: investmentsSuccess } = investmentsFormSchema.safeParse(investments);
  const { success: poolsSuccess } = poolsFormSchema.safeParse(pools);

  const isValidValuation = Boolean(
    parsedValuation.success && parsedValuation.data.date && parsedValuation.data.preMoneyValuation,
  );
  const isValidInvestments = investments?.investmentItems ? investmentsSuccess : true;
  const isValidCustomSafes = customSafes?.safeItems ? customSafesSuccess : true;
  const isValidPools = pools?.poolItems ? poolsSuccess : true;

  const isUpdateRequired = Boolean(scheduled[scenario?.id || '']);
  // A hypothetical SAFE the scenario refused (e.g. owning 100 % or more) stays on screen unsaved;
  // generating now would quietly leave it out.
  const hasUnsavedCustomSafes = Boolean(customSafes?.safeItems?.some((item) => !item?.id));

  const isValid =
    isValidValuation &&
    isValidInvestments &&
    isValidCustomSafes &&
    isValidPools &&
    !hasUnsavedCustomSafes &&
    !isConversionRefused;

  return (
    <>
      <div className="flex h-full shrink-0 flex-col gap-2 rounded-lg border-[1px] border-brand-50 bg-brand-25 px-1 py-3 lg:w-[334px]">
        <span className="px-4 pb-1 text-base font-semibold text-brand-700">
          <AppFormattedMessage id="CONVERSION_SUMMARY" />
        </span>
        <div className="flex flex-col gap-2 rounded-md bg-white p-2 shadow-sm">
          <div className="flex w-full flex-wrap divide-y divide-gray-100 rounded-lg bg-gray-50 py-2 lg:flex-col lg:flex-nowrap">
            <ConversionSummaryItem
              currencySign={currencySign}
              name={<AppFormattedMessage id="SHARE_PRICE" />}
              value={toMathRound(conversionSummary?.sharePrice)}
            />
            {scenario?.type === ScenarioType.FUNDRAISING ? (
              <>
                <ConversionSummaryItem
                  currencySign={currencySign}
                  name={<AppFormattedMessage id="POST_MONEY_VALUATION" />}
                  value={conversionSummary?.postMoneyValuation}
                />
                <ConversionSummaryItem
                  currencySign={currencySign}
                  name={<AppFormattedMessage id="INVESTMENT" />}
                  value={conversionSummary?.investments}
                />
                <ConversionSummaryItem
                  name={<AppFormattedMessage id="SHARES_ISSUED_DURING_SCENARIO" />}
                  value={conversionSummary?.sharesIssuedDuringScenario}
                />
                <ConversionSummaryItem
                  currencySign={currencySign}
                  name={<AppFormattedMessage id="SAFE_INVESTMENTS" />}
                  value={conversionSummary?.safeInvestments}
                />
                <ConversionSummaryItem
                  name={<AppFormattedMessage id="SHARES_ISSUED_AFTER_SCENARIO" />}
                  value={conversionSummary?.issuedShares}
                />
              </>
            ) : (
              <>
                <ConversionSummaryItem
                  name={<AppFormattedMessage id="OWNERSHIP_PERCENT" />}
                  value={toMathRound(
                    (conversionSummary?.stakeholderOwnershipPercentage || 0) * 100,
                  )}
                />
                <ConversionSummaryItem
                  name={<AppFormattedMessage id="MY_LIQUIDITY_PROCEEDS" />}
                  value={conversionSummary?.stakeholderLiquidityProceeds}
                />
              </>
            )}
          </div>
          {isScenarioHadCapTable &&
            (isUpdateRequired ? (
              <div className="flex flex-col divide-y divide-sunbeam-100 rounded-lg border-[1px] border-sunbeam-100 bg-sunbeam-50 p-4">
                <div className="flex w-full flex-col items-center gap-2 pb-2">
                  <span className="rounded-2xl bg-sunbeam-100 px-2 py-[2px] text-label-md font-medium text-sunbeam-600">
                    <AppFormattedMessage id="REPORT" />
                  </span>
                  <span className="text-center text-label-sm font-medium text-gray-500">
                    <AppFormattedMessage id="DATA_IN_CAP_TABLE_HAS_BEEN_CHANGED" />
                  </span>
                </div>
                <span className="pt-2 text-center text-label-sm font-medium text-gray-700">
                  <AppFormattedMessage id="UPDATE_YOUR_SCENARIO_RECOMMENDATION" />
                </span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2 rounded-lg border-[1px] border-sunbeam-100 bg-sunbeam-50 p-4">
                <span className="rounded-2xl bg-sunbeam-100 px-2 py-[2px] text-label-md font-medium text-sunbeam-600">
                  Report
                </span>
                <Button
                  className={twMerge(
                    'items-center gap-2 text-sm font-[450] text-brand-700 disabled:bg-transparent disabled:ring-transparent',
                    pointerBlockedStyles,
                  )}
                  disabled={Boolean(pointerBlockedStyles)}
                  isLoading={isCapTablePending}
                  onClick={onOpenCapTable}
                  styleType="NONE"
                >
                  <MaximizeIcon />
                  <AppFormattedMessage id="VIEW_DETAILED_SCENARIO" />
                </Button>
                <span className="pt-2 text-center text-label-sm font-medium text-gray-700">
                  <AppFormattedMessage id="LAST_GENERATION" />:{' '}
                  {capTableData?.[0]?.createdAt
                    ? format(capTableData[0].createdAt, 'dd/MM/yyyy')
                    : '-'}
                </span>
              </div>
            ))}
        </div>
        <div
          className={twMerge(
            'flex items-center gap-2 rounded-md bg-white p-2 shadow-sm',
            isTablet && 'justify-end',
          )}
        >
          <Button
            className={twMerge(
              'h-full bg-brand-700 px-6 py-[10px] text-sm font-[550] text-white',
              isTablet ? 'w-[219px]' : 'w-full',
              pointerBlockedStyles,
            )}
            disabled={
              !isValid ||
              (!isUpdateRequired && isScenarioHadCapTable) ||
              Boolean(pointerBlockedStyles)
            }
            isLoading={isLoadingSummary || isCapTableLoading}
            onClick={onOpenCapTable}
            styleType="NONE"
          >
            {isScenarioHadCapTable ? (
              <AppFormattedMessage id="UPDATE_SCENARIO" />
            ) : (
              <AppFormattedMessage id="GENERATE_SCENARIO" />
            )}
          </Button>
        </div>
      </div>
    </>
  );
};

export type ConversionSummaryItemProps = {
  currencySign?: string;
  name: ReactNode | string;
  percentage?: boolean;
  value?: number;
};

export const ConversionSummaryItem: FC<ConversionSummaryItemProps> = ({
  value,
  name,
  currencySign,
  percentage,
}) => {
  return (
    <div className="flex min-w-[288px] flex-col gap-1 px-3 py-2">
      <span className="text-xs font-[450] text-gray-500">{name}</span>
      <span
        className={twMerge(
          'text-sm',
          value ? 'font-[550] text-gray-800' : 'font-[450] text-gray-400',
        )}
      >
        {currencySign}
        {value ? value.toLocaleString('en-US') : '00.00'}
        {percentage && '%'}
      </span>
    </div>
  );
};
